import React, { Component } from 'react';
import PropTypes from 'prop-types';
import update from 'immutability-helper';
import { DatePicker } from 'antd';
import moment from 'moment';

import i18n from '../../config/i18n';
import connectActionsAndReducers from '../../toolbox/connectActionsAndReducers';
import { PatientAPIConnectors } from '../../api/index';
import Link from '../../components/shared/Link';
import {
  REGEX_IDENTIFICATIONID,
  REGEX_NAME,
  REGEX_PHONE,
} from '../../toolbox/regex';
import { DATE_OF_BIRTH_FORMAT } from '../../constants/DateOfBirthFormat';

const link = {
  textDecoration: 'none',
  color: 'inherit',
};

class CreatePatientContainer extends Component {
  static propTypes = {
    actions: PropTypes.shape({
      createSuccessAlert: PropTypes.func.isRequired,
      createDangerAlert: PropTypes.func.isRequired,
    }).isRequired,
    router: PropTypes.shape({
      navigate: PropTypes.func.isRequired,
    }).isRequired,
  };

  state = {
    patient: {
      identification: '',
      firstName: '',
      lastName: '',
      gender: '',
      phone: '',
      dateOfBirth: null,
      address: {},
      emergencyContact: {},
    },
    isIdValid: true,
    isFirstNameValid: true,
    isLastNameValid: true,
    isPhoneValid: true,
  };

  handleInputOrSelectChange = (e) => {
    const fieldName = e.target.name;
    const newField = {
      [fieldName]: { $set: e.target.value },
    };
    const newPatient = update(this.state.patient, newField);
    this.setState({ patient: newPatient });
  };

  handleDOBChange = (date) => {
    const newPatient = update(this.state.patient, {
      dateOfBirth: { $set: date.startOf('day').format(DATE_OF_BIRTH_FORMAT) },
    });
    this.setState({ patient: newPatient });
  };

  create = () => {
    const {
      actions: {
        createSuccessAlert,
        createDangerAlert,
      },
      router,
    } = this.props;
    const patient = this.prepareToCreate();
    PatientAPIConnectors.createPatient(patient)
      .then(() => {
        createSuccessAlert(i18n.t('createSucceedMsg_patient'));
        setTimeout(() => {
          router.navigate('/medical-records', { trigger: true });
        }, 1000);
      })
      .catch((err) => {
        createDangerAlert(err.message);
      });
  };

  isButtonEnabled = () => (
    this.state.patient.identification
    && this.state.isIdValid
    && this.state.isFirstNameValid
    && this.state.patient.firstName
    && this.state.isLastNameValid
    && this.state.patient.lastName
    && this.state.patient.dateOfBirth
  );

  prepareToCreate = () => {
    // Some additional treatment to dateOfBirth, firstName and lastName
    // before creating patient.
    const { patient } = this.state;
    const dateOfBirth = patient.dateOfBirth;
    const firstName = patient.firstName.toLowerCase();
    const lastName = patient.lastName.toLowerCase();
    return update(patient, {
      dateOfBirth: { $set: dateOfBirth },
      firstName: { $set: firstName },
      lastName: { $set: lastName },
    });
  };

  render() {
    const {
      patient,
      isIdValid,
      isFirstNameValid,
      isLastNameValid,
      isPhoneValid,
    } = this.state;
    const {
      router,
    } = this.props;
    // TODO(kwang) I feel like we should avoid duplicated codes here
    return (
      <div className="well center col-md-8 col-md-offset-2">
        <form className="form-horizontal">
          <fieldset>
            <legend>
              {i18n.t('patientInfo')}
            </legend>
            <div className="form-group">
              <label htmlFor="inputID" className="col-lg-2 control-label">
                {i18n.t('ssn_taiwanId')} *
              </label>
              <div className="col-lg-10">
                <input
                  type="text"
                  className="form-control"
                  id="inputID"
                  name="identification"
                  placeholder={i18n.t('placeholder.taiwanId')}
                  required
                  onChange={this.handleInputOrSelectChange}
                  onBlur={() => (
                    REGEX_IDENTIFICATIONID.test(patient.identification)
                      ? this.setState({ isIdValid: true })
                      : this.setState({ isIdValid: false })
                  )}
                />
                {
                  patient.identification && !isIdValid
                    ? (
                      <span
                        style={{ color: 'red' }}
                        title={i18n.t('ssnIdRules')}
                      >
                        {i18n.t('invalidIdMsg')}
                      </span>
                    )
                    : null
                }
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="inputFirstName" className="col-lg-2 control-label">
                {i18n.t('firstName')} *
              </label>
              <div className="col-lg-10">
                <input
                  type="text"
                  className="form-control"
                  id="inputFirstName"
                  name="firstName"
                  placeholder={i18n.t('placeholder.firstName')}
                  required
                  onChange={this.handleInputOrSelectChange}
                  onBlur={() => (
                    REGEX_NAME.test(patient.firstName)
                      ? this.setState({ isFirstNameValid: true })
                      : this.setState({ isFirstNameValid: false })
                  )}
                />
                {
                  isFirstNameValid
                    ? null
                    : (
                      <span style={{ color: 'red' }}>
                        {i18n.t('invalidFirstNameMsg')}
                      </span>
                    )
                }
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="inputLastName" className="col-lg-2 control-label">
                {i18n.t('lastName')} *
              </label>
              <div className="col-lg-10">
                <input
                  type="text"
                  className="form-control"
                  id="inputLastName"
                  name="lastName"
                  placeholder={i18n.t('placeholder.lastName')}
                  required
                  onChange={this.handleInputOrSelectChange}
                  onBlur={() => (
                    REGEX_NAME.test(patient.lastName)
                      ? this.setState({ isLastNameValid: true })
                      : this.setState({ isLastNameValid: false })
                  )}
                />
                {
                  isLastNameValid
                    ? null
                    : (
                      <span style={{ color: 'red' }}>
                        {i18n.t('invalidLastNameMsg')}
                      </span>
                    )
                }
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="inputPhone" className="col-lg-2 control-label">
                {i18n.t('phoneNumber')}
              </label>
              <div className="col-lg-10">
                <input
                  type="text"
                  className="form-control"
                  id="inputPhone"
                  name="phone"
                  placeholder={i18n.t('placeholder.phone')}
                  onChange={this.handleInputOrSelectChange}
                  onBlur={() => (
                    REGEX_PHONE.test(patient.phone)
                      ? this.setState({ isPhoneValid: true })
                      : this.setState({ isPhoneValid: false })
                  )}
                />
                {
                  patient.phone && !isPhoneValid
                    ? (
                      <span style={{ color: 'red' }}>
                        {i18n.t('invalidPhoneMsg')}
                      </span>
                    )
                    : null
                }
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="selectGender" className="col-lg-2 control-label">
                {i18n.t('gender')}
              </label>
              <div className="col-lg-10">
                <select
                  className="form-control"
                  id="selectGender"
                  name="gender"
                  onChange={this.handleInputOrSelectChange}
                >
                  <option value="">-- {i18n.t('select')} --</option>
                  <option value="M">{i18n.t('male')}</option>
                  <option value="F">{i18n.t('female')}</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="selectDateOfBirth" className="col-lg-2 control-label">
                {i18n.t('d_o_b')} *
              </label>
              <div className="col-lg-10 selectDOB">
                <DatePicker
                  id="selectDateOfBirth"
                  value={patient.dateOfBirth ? moment(patient.dateOfBirth) : null}
                  disabledDate={date => date && date > moment().endOf('day')}
                  onChange={this.handleDOBChange}
                />
              </div>
            </div>
          </fieldset>
          <div className="form-group">
            <div className="col-lg-12">
              <input
                className="btn btn-success col-lg-offset-8 col-lg-2"
                onClick={this.create}
                disabled={!this.isButtonEnabled()}
                type="button"
                value={i18n.t('create')}
              />
              <Link
                href="/medical-records"
                style={link}
                router={router}
              >
                <button className="btn btn-default col-lg-2">
                  {i18n.t('cancel')}
                </button>
              </Link>
            </div>
          </div>
        </form>
      </div>
    );
  }
}

export default connectActionsAndReducers(CreatePatientContainer);



// WEBPACK FOOTER //
// ./src/containers/patient/CreatePatientContainer.js